import { useEffect, useRef, useState } from 'react';
import { playDiceRoll } from '../sound';

/** How long our own roll tumbles before the reveal. */
const OWN_MS = 950;
/** Remote rolls tumble a little shorter so play keeps moving. */
const REMOTE_MS = 700;

/** 3×3 pip centres in a 100-unit face, spread wide so the pips never crowd. */
const GRID: [number, number][] = [
  [30, 30], [50, 30], [70, 30],
  [30, 50], [50, 50], [70, 50],
  [30, 70], [50, 70], [70, 70],
];

const FACE_PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 3, 6, 2, 5, 8],
};

/** Base cube rotation that brings each face to the front, upright. */
const FACE_ROT: Record<number, { x: number; y: number }> = {
  1: { x: 0, y: 0 },
  2: { x: -90, y: 0 },
  3: { x: 0, y: -90 },
  4: { x: 0, y: 90 },
  5: { x: 90, y: 0 },
  6: { x: 0, y: 180 },
};

/** Where each face sits inside the cube (rotate outward, then push out). */
const FACE_TRANSFORM: Record<number, string> = {
  1: 'rotateY(0deg) translateZ(42px)',
  2: 'rotateX(90deg) translateZ(42px)',
  3: 'rotateY(90deg) translateZ(42px)',
  4: 'rotateY(-90deg) translateZ(42px)',
  5: 'rotateX(-90deg) translateZ(42px)',
  6: 'rotateY(180deg) translateZ(42px)',
};

function randomSpins() {
  return { x: 2 + Math.floor(Math.random() * 3), y: 2 + Math.floor(Math.random() * 3) };
}

/** Cube transform for a value after `spins` full turns per axis. */
function rotationFor(v: number, spins: { x: number; y: number }) {
  const base = FACE_ROT[v] ?? FACE_ROT[1];
  return `rotateX(${spins.x * 360 + base.x}deg) rotateY(${spins.y * 360 + base.y}deg)`;
}

interface DiceProps {
  value: number;
  /** Changes on every throw, even when the face repeats (4 then 4). */
  rollId?: number;
  canRoll?: boolean;
  onRoll?: () => void;
}

/** A real CSS 3D die. Tumbles on your own click and on remote rolls, then
 *  lands with the rolled face shown upright. */
export default function Dice({ value, rollId, canRoll, onRoll }: DiceProps) {
  const [display, setDisplay] = useState(value > 0 ? value : 1);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(() => rotationFor(display, { x: 0, y: 0 }));

  const spinningRef = useRef(false);
  const spinsRef = useRef({ x: 0, y: 0 });
  const lastOwnSpinAt = useRef(0);
  const displayRef = useRef(display);
  const settleTimer = useRef<number | null>(null);
  const seenRollId = useRef<number | undefined>(undefined);

  const setTarget = (v: number, spins: { x: number; y: number }) => {
    displayRef.current = v;
    setDisplay(v);
    setRotation(rotationFor(v, spins));
  };

  const scheduleSettle = (ms: number) => {
    if (settleTimer.current) window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      spinningRef.current = false;
      setSpinning(false);
    }, ms);
  };

  useEffect(() => {
    return () => {
      if (settleTimer.current) window.clearTimeout(settleTimer.current);
    };
  }, []);

  // New dice value from the server.
  //  - Our own roll: retarget the tumbling cube to the real result (same spins).
  //  - Anyone else / initial load: do a brief tumble so everyone sees it roll.
  useEffect(() => {
    if (value <= 0) return;
    if (rollId !== undefined && rollId === seenRollId.current) return;
    seenRollId.current = rollId;

    const isOwnRoll = spinningRef.current || Date.now() - lastOwnSpinAt.current < OWN_MS + 600;
    if (isOwnRoll) {
      spinningRef.current = true;
      setSpinning(true);
      setTarget(value, spinsRef.current);
      scheduleSettle(OWN_MS);
    } else {
      if (value !== displayRef.current) playDiceRoll();
      spinningRef.current = true;
      setSpinning(true);
      spinsRef.current = randomSpins();
      setTarget(value, spinsRef.current);
      scheduleSettle(REMOTE_MS);
    }
  }, [value, rollId]);

  const handleClick = () => {
    if (!canRoll || !onRoll || spinningRef.current) return;
    playDiceRoll();
    // Start tumbling immediately; the server result retargets us mid-air.
    spinningRef.current = true;
    lastOwnSpinAt.current = Date.now();
    spinsRef.current = randomSpins();
    setSpinning(true);
    setTarget(displayRef.current, spinsRef.current);
    onRoll();
    scheduleSettle(OWN_MS);
  };

  const active = !!onRoll && !!canRoll;

  return (
    <div className="relative shrink-0">
      {active && (
        <span className="snk-glow-pulse pointer-events-none absolute -inset-2 rounded-2xl bg-[#ffd257]/30 blur-lg" aria-hidden="true" />
      )}

      <button
        type="button"
        onClick={handleClick}
        aria-label={spinning ? 'Rolling…' : canRoll ? 'Roll the dice' : `Last roll: ${display}`}
        className="dice-scene relative block"
        style={{ width: 84, height: 84 }}
      >
        <div className="dice-cube" style={{ transform: rotation }}>
          {[1, 2, 3, 4, 5, 6].map((v) => (
            <div key={v} className="dice-face" style={{ transform: FACE_TRANSFORM[v] }}>
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" aria-hidden="true">
                {FACE_PIPS[v].map((i) => (
                  <circle key={i} cx={GRID[i][0]} cy={GRID[i][1]} r="8" fill="#5a3812" />
                ))}
              </svg>
              <span
                className="pointer-events-none absolute inset-0"
                style={{ borderRadius: '18%', background: 'radial-gradient(circle at 30% 24%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 52%)' }}
                aria-hidden="true"
              />
            </div>
          ))}
        </div>
      </button>

      {/* Ground shadow */}
      <span
        className="pointer-events-none absolute -bottom-3 left-1/2 h-3 w-16 -translate-x-1/2 rounded-full bg-black/45 blur-md transition-all duration-300"
        style={spinning ? { transform: 'translateX(-50%) scale(1.15)', opacity: 0.6 } : undefined}
        aria-hidden="true"
      />

      <span
        className="pointer-events-none absolute inset-x-0 -bottom-9 mx-auto w-fit rounded-full bg-black/30 px-2 py-0.5 text-[10px] font-bold backdrop-blur-sm"
        style={{ color: active ? '#ffe58a' : 'rgba(220,210,190,0.6)' }}
      >
        {spinning ? 'rolling…' : canRoll ? 'roll me!' : display > 0 ? `rolled ${display}` : 'roll'}
      </span>
    </div>
  );
}